import { type CompanyStatisticsListingAttribution } from './company-statistics.types';
import {
  TransportMode,
  ProviderType,
  Tier,
  ClaimStatus,
  AnnualVolumeTeu,
  EmployeeCountRange,
} from './provider.types';
import { CoverageDirection } from './coverage.types';
import { GeoObjectType } from './geo-object.types';
import { SeoPageVariant } from './seo-page.types';

// ==================== HUB PAGE CONTEXT ====================

/**
 * Types of hub listing pages
 */
export enum HubPageType {
  /** Single geo location, e.g. /hub/poland */
  geo_location = 'geo_location',
  /** Geo + direction, e.g. /hub/poland/import */
  geo_direction = 'geo_direction',
  /** Geo + direction + mode, e.g. /hub/poland/import/ocean */
  geo_direction_mode = 'geo_direction_mode',
  /** Route between two locations, e.g. /hub/poland/germany */
  route = 'route',
  /** Route + mode, e.g. /hub/poland/germany/ocean */
  route_mode = 'route_mode',
}

/**
 * Parsed context from hub URL segments
 */
export interface HubPageContext {
  pageType: HubPageType;
  /** Content/listing variant rendered at the same route context. */
  pageVariant?: SeoPageVariant;
  /** Primary geo object (for single location pages) */
  geoObject?: HubGeoObjectInfo;
  /** Origin geo object (for route pages) */
  originGeo?: HubGeoObjectInfo;
  /** Destination geo object (for route pages) */
  destinationGeo?: HubGeoObjectInfo;
  /** Trade direction filter */
  direction?: CoverageDirection;
  /** Transport mode filter */
  transportMode?: TransportMode;
}

/**
 * Minimal geo object info needed for hub pages
 */
export interface HubGeoObjectInfo {
  id: string;
  name: string;
  slug: string;
  type: GeoObjectType;
  unlocode: string | null;
  isoCountryCode?: string | null;
  /** Country slug for ports - used for port URL generation */
  countrySlug?: string | null;
}

// ==================== HUB PROVIDERS REQUEST/RESPONSE ====================

/**
 * Parameters for fetching hub providers
 */
export interface GetHubProvidersParams {
  /** Filter by geo object coverage */
  geoObjectId?: string;
  /** Filter by route origin */
  originGeoObjectId?: string;
  /** Filter by route destination */
  destinationGeoObjectId?: string;
  /** Filter by coverage direction */
  direction?: CoverageDirection;
  /** Filter by transport mode */
  transportMode?: TransportMode;
  /** Pagination page (0-indexed) */
  page?: number;
  /** Items per page */
  limit?: number;
  /** Language code for translations */
  lang?: string;
}

/**
 * Provider data for hub listing pages
 */
export interface HubProviderDto {
  statistics?: CompanyStatisticsListingAttribution;
  companyName: string;
  slug: string;
  providerTypes: ProviderType[];
  transportModes: TransportMode[];
  tier: Tier;
  claimStatus: ClaimStatus;
  description: string | null;
  logoUrl: string | null;
  /** Light/inverted logo — use dark preview frame in listings */
  isLight?: boolean;
  tagline: string | null;
  foundedYear: number | null;
  annualVolumeTeu: AnnualVolumeTeu | null;
  employeeCountRange: EmployeeCountRange | null;
  /** Shipment types (e.g., FCL, LCL) */
  shipmentTypes: { code: string }[];
  /** Primary office location for display */
  location?: {
    countryName: string | null;
  };
}

/**
 * Paginated response for hub providers
 */
export interface HubProvidersResponseDto {
  data: HubProviderDto[];
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ==================== HUB PAGE CONTENT ====================

/**
 * Dynamic page content based on context
 */
export interface HubPageContentDto {
  /** Page title for H1 */
  title: string;
  /** Page title for meta/SEO (typically "Top ..." variant) */
  pageTitle: string;
  /** Meta description for SEO */
  metaDescription: string;
  /** Main content paragraph */
  mainContent: string;
  /** Section title for H2 above provider list */
  listTitle: string;
  /** Breadcrumb items */
  breadcrumbs: HubBreadcrumbItem[];
  /** Whether the page is published. False when an unpublished SEO page exists for this context. */
  isPublished?: boolean;
}

/**
 * Breadcrumb item
 */
export interface HubBreadcrumbItem {
  label: string;
  href?: string;
}

// Note: HubStats, FeaturedProvider, and HubData are defined in search.types.ts
// Re-export them here for convenience
export type { HubStats, FeaturedProvider, HubData } from './search.types';

// ==================== HUB PORTS PAGE TYPES ====================

/**
 * Types of port hub pages
 */
export enum PortPageType {
  /** All ports list, e.g. /hub/ports */
  ports_list = 'ports_list',
  /** Ports by geo, e.g. /hub/ports/germany */
  ports_geo = 'ports_geo',
  /** Single port detail, e.g. /hub/ports/port-of-hamburg */
  port_detail = 'port_detail',
}

/**
 * Port data for hub listing pages
 */
export interface HubPortDto {
  id: string;
  name: string;
  slug: string;
  unlocode: string | null;
  countryName: string | null;
  countrySlug: string | null;
  coordinates: [number, number] | null;
  alternativeNames?: string[];
}

/**
 * Paginated response for hub ports
 */
export interface PortsListResponseDto {
  data: HubPortDto[];
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Port detail response with providers
 */
export interface PortDetailResponseDto {
  port: HubPortDto;
  providers: HubProvidersResponseDto;
}

/**
 * Dynamic page content for port pages
 */
export interface HubPortPageContentDto {
  /** Page title for H1 */
  title: string;
  /** Page title for meta/SEO */
  pageTitle: string;
  /** Meta description for SEO */
  metaDescription: string;
  /** Main content paragraph */
  mainContent: string;
  /** Section title for H2 */
  listTitle: string;
  /** Breadcrumb items */
  breadcrumbs: HubBreadcrumbItem[];
  /** Whether the page is published. False when an unpublished SEO page exists for this context. */
  isPublished?: boolean;
}

/**
 * Parameters for fetching hub ports
 */
export interface GetHubPortsParams {
  /** Pagination page (0-indexed) */
  page?: number;
  /** Items per page */
  limit?: number;
  /** Language code for translations */
  lang?: string;
}

// ==================== HUB INDEX PAGE TYPES ====================

/**
 * Available page for a geo (direction + mode combination with published SEO page)
 */
export interface HubIndexAvailablePage {
  direction?: CoverageDirection;
  transportMode?: TransportMode;
}

/**
 * Geo entry for hub index page (trade region or country)
 */
export interface HubIndexGeoEntry {
  name: string;
  slug: string;
  type: 'trade_regions' | 'country';
  /** Available sub-pages with published SEO pages */
  availablePages: HubIndexAvailablePage[];
  /** Whether this geo has a published ports page (for countries) */
  hasPortsPage?: boolean;
}

/**
 * Route entry for hub index page
 */
export interface HubIndexRouteEntry {
  originName: string;
  originSlug: string;
  destinationName: string;
  destinationSlug: string;
  /** Available transport modes with published SEO pages */
  availableModes: TransportMode[];
  hasProvidersPage: boolean;
  hasRatesPage: boolean;
}

/**
 * Hub index page data response
 */
export interface HubIndexDataDto {
  tradeRegions: HubIndexGeoEntry[];
  countries: HubIndexGeoEntry[];
  routes: HubIndexRouteEntry[];
}
